/**
 * dp Video Content player bootstrap
 *
 * YouTube implementation only (for now).
 * TODO: Vimeo
 * TODO: Wistia
 *
 * Supports multiple block instances on the same page.
 */

import "../css/dp-video-content-player.css";

(function () {
  const ROOT_SELECTOR = ".dp-video-content";
  const PAYLOAD_SELECTOR =
    'script.dp-video-content__payload[type="application/json"]';

  function safeParseJson(text) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  function loadYouTubeIframeApi() {
    if (window.YT && window.YT.Player) {
      return Promise.resolve(window.YT);
    }

    if (window.__dpvcYouTubeApiPromise) {
      return window.__dpvcYouTubeApiPromise;
    }

    window.__dpvcYouTubeApiPromise = new Promise((resolve, reject) => {
      let resolved = false;

      function tryResolve() {
        if (resolved) return;
        if (window.YT && window.YT.Player) {
          resolved = true;
          resolve(window.YT);
        }
      }

      tryResolve();

      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof previous === "function") {
          try {
            previous();
          } catch (e) {
            // ignore
          }
        }
        tryResolve();
      };

      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        tryResolve();
        if (resolved) {
          window.clearInterval(timer);
          return;
        }
        if (Date.now() - startedAt > 15000) {
          window.clearInterval(timer);
          reject(new Error("YouTube Iframe API timed out"));
        }
      }, 100);
    });

    return window.__dpvcYouTubeApiPromise;
  }

  let instanceCounter = 0;

  function shouldRenderExternalControls(root) {
    return root && root.getAttribute("data-dpvc-controls") === "1";
  }

  function buildControls() {
    const controls = document.createElement("div");
    controls.className = "dpvc-player__controls";

    const btnPlay = document.createElement("button");
    btnPlay.type = "button";
    btnPlay.className = "dpvc-player__button";
    btnPlay.setAttribute("data-dpvc-action", "toggle-play");
    btnPlay.textContent = "Play";

    const btnMute = document.createElement("button");
    btnMute.type = "button";
    btnMute.className = "dpvc-player__button";
    btnMute.setAttribute("data-dpvc-action", "toggle-mute");
    btnMute.textContent = "Mute";

    controls.appendChild(btnPlay);
    controls.appendChild(btnMute);

    return controls;
  }

  function buildPoster(payload) {
    const src =
      payload && payload.posterSrc ? String(payload.posterSrc).trim() : "";
    if (!src) return null;

    const poster = document.createElement("button");
    poster.type = "button";
    poster.className = "dpvc-player__poster";
    poster.setAttribute("data-dpvc-poster", "");
    poster.setAttribute("aria-label", "Play video");
    const safeSrc = src.replace(/"/g, '\\"');
    poster.style.backgroundImage = `url("${safeSrc}")`;

    const icon = document.createElement("span");
    icon.className = "dpvc-player__poster-icon";
    icon.setAttribute("aria-hidden", "true");
    poster.appendChild(icon);

    return poster;
  }

  function buildShell(root, payload) {
    if (root.querySelector("[data-dpvc-player-root]")) {
      return root.querySelector("[data-dpvc-player-root]");
    }

    const shell = document.createElement("div");
    shell.className = "dpvc-player";
    shell.setAttribute("data-dpvc-player-root", "");

    const frame = document.createElement("div");
    frame.className = "dpvc-player__frame";
    frame.setAttribute("data-dpvc-frame", "");

    const frameInner = document.createElement("div");
    frameInner.className = "dpvc-player__frame-inner";
    frame.appendChild(frameInner);

    const poster = buildPoster(payload);
    if (poster) {
      frameInner.appendChild(poster);
    }

    const mount = document.createElement("div");
    mount.className = "dpvc-player__mount";
    mount.id = `dpvc-yt-${++instanceCounter}`;
    frameInner.appendChild(mount);

    shell.appendChild(frame);

    if (shouldRenderExternalControls(root)) {
      shell.appendChild(buildControls());
    }

    root.appendChild(shell);
    return shell;
  }

  function initYouTube(root, payload) {
    const shell = buildShell(root, payload);
    const mount = shell.querySelector(".dpvc-player__mount");
    const playBtn = shell.querySelector('[data-dpvc-action="toggle-play"]');
    const muteBtn = shell.querySelector('[data-dpvc-action="toggle-mute"]');
    const poster = shell.querySelector(".dpvc-player__poster");

    if (!mount) return;

    let pendingPlay = false;
    let player = null;

    function hidePoster() {
      if (!poster) return;
      poster.classList.add("is-hidden");
    }

    if (poster) {
      poster.addEventListener("click", () => {
        pendingPlay = true;
        hidePoster();
        try {
          if (player) player.playVideo();
        } catch (e) {
          // ignore
        }
      });
    }

    loadYouTubeIframeApi()
      .then(() => {
        player = new window.YT.Player(mount.id, {
          videoId: payload.videoId,
          playerVars: {
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
          },
          events: {
            onReady: () => {
              if (pendingPlay) {
                try {
                  player.playVideo();
                } catch (e) {
                  // ignore
                }
              }

              if (muteBtn) {
                try {
                  muteBtn.textContent = player.isMuted() ? "Unmute" : "Mute";
                } catch (e) {
                  // ignore
                }
              }
            },
            onStateChange: (evt) => {
              if (playBtn && evt && typeof evt.data === "number") {
                if (evt.data === 1) playBtn.textContent = "Pause";
                if (evt.data === 2) playBtn.textContent = "Play";
              }

              if (evt && typeof evt.data === "number" && evt.data === 1) {
                hidePoster();
              }
            },
          },
        });

        if (playBtn) {
          playBtn.addEventListener("click", () => {
            try {
              const state = player.getPlayerState();
              if (state === 1) {
                player.pauseVideo();
              } else {
                hidePoster();
                player.playVideo();
              }
            } catch (e) {
              // ignore
            }
          });
        }

        if (muteBtn) {
          muteBtn.addEventListener("click", () => {
            try {
              if (player.isMuted()) {
                player.unMute();
                muteBtn.textContent = "Mute";
              } else {
                player.mute();
                muteBtn.textContent = "Unmute";
              }
            } catch (e) {
              // ignore
            }
          });
        }
      })
      .catch(() => {
        const link = document.createElement("a");
        link.href = `https://www.youtube.com/watch?v=${encodeURIComponent(
          payload.videoId,
        )}`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = "Open video on YouTube";
        root.appendChild(link);
      });
  }

  function initOne(root) {
    const payloadEl = root.querySelector(PAYLOAD_SELECTOR);
    const payload =
      payloadEl ? safeParseJson(payloadEl.textContent || "") : null;
    if (!payload || !payload.videoId) return;

    const platform = (payload.platform || "youtube").toLowerCase();
    if (platform === "youtube") {
      initYouTube(root, payload);
      return;
    }
  }

  function initAll() {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    if (!roots.length) return;
    roots.forEach(initOne);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
